import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import SubscriptionStatus from "../../components/subscription/SubscriptionStatus";
import SidebarLayout from "../shops/SidebarLayout";
import { getSubscriptionAccessView } from "../../utils/subscriptionAccessState";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL;

const BusinessSubscription = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [checkoutLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [locations, setLocations] = useState([]);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showRemoveLocationModal, setShowRemoveLocationModal] = useState(false);
  const [selectedLocationToRemove, setSelectedLocationToRemove] =
    useState(null);

  useEffect(() => {
    // Check for return from Stripe Checkout
    const params = new URLSearchParams(location.search);

    if (params.get("success") === "true") {
      const sessionId = params.get("session_id");

      setMessage({
        type: "success",
        text: "🎉 Subscription activated successfully! Welcome aboard!",
      });

      // Clean up URL
      navigate("/dashboard/settings/subscription", { replace: true });

      // Sync subscription from Stripe (in case webhook hasn't processed yet)
      if (sessionId) {
        syncSubscriptionFromCheckout(sessionId);
      } else {
        // Poll for subscription status update if no session ID
        pollForSubscriptionUpdate();
      }
    } else if (params.get("canceled") === "true") {
      setMessage({
        type: "error",
        text: "Checkout canceled. You can subscribe anytime to continue using the platform.",
      });
      // Clean up URL
      navigate("/dashboard/settings/subscription", { replace: true });
    }

    fetchSubscriptionStatus();
  }, []);

  const syncSubscriptionFromCheckout = async (sessionId) => {
    const token = localStorage.getItem("ogestyle_token");
    try {
      await axios.post(
        `${API_BASE_URL}/api/subscription-new/sync-from-checkout`,
        { sessionId },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Wait a moment then refetch status
      setTimeout(() => {
        fetchSubscriptionStatus();
      }, 1000);
    } catch (error) {
      // Fallback to polling
      pollForSubscriptionUpdate();
    }
  };

  const pollForSubscriptionUpdate = () => {
    let pollCount = 0;
    const pollInterval = setInterval(async () => {
      pollCount++;
      const token = localStorage.getItem("ogestyle_token");
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/subscription-new/status`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        // Check if subscription is active
        if (
          response.data.hasSubscription &&
          response.data.status === "active"
        ) {
          clearInterval(pollInterval);
          setSubscription(response.data);
          setLocations(response.data.locationSubscriptions || []);
        }
      } catch (error) {
      }

      // Stop polling after 10 attempts (10 seconds)
      if (pollCount >= 10) {
        clearInterval(pollInterval);
      }
    }, 1000);
  };

  const fetchSubscriptionStatus = async () => {
    const token = localStorage.getItem("ogestyle_token");
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/subscription-new/status`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSubscription(response.data);
      // Use shopLocations from API response (actual ShopLocation records)
      setLocations(response.data.shopLocations || []);
    } catch (error) {
      setMessage({
        type: "error",
        text: "Failed to load subscription details",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    const token = localStorage.getItem("ogestyle_token");
    try {
      await axios.post(
        `${API_BASE_URL}/api/subscription-new/reactivate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setMessage({
        type: "success",
        text: "Subscription reactivated successfully!",
      });

      fetchSubscriptionStatus();
    } catch (error) {
      setMessage({
        type: "error",
        text:
          error.response?.data?.error || "Failed to reactivate subscription",
      });
    }
  };

  const handleCancelSubscription = async () => {
    const token = localStorage.getItem("ogestyle_token");
    try {
      await axios.post(
        `${API_BASE_URL}/api/subscription-new/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setShowCancelModal(false);
      setMessage({
        type: "success",
        text: "Subscription canceled successfully. You'll have access until the end of your billing period.",
      });

      fetchSubscriptionStatus();
    } catch (error) {
      setShowCancelModal(false);
      setMessage({
        type: "error",
        text: error.response?.data?.error || "Failed to cancel subscription",
      });
    }
  };

  const handleRemoveLocation = async () => {
    const token = localStorage.getItem("ogestyle_token");
    if (!selectedLocationToRemove) return;

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/subscription-new/remove-location`,
        { locationId: selectedLocationToRemove.id },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setShowRemoveLocationModal(false);
      setSelectedLocationToRemove(null);
      setMessage({
        type: "success",
        text: `${selectedLocationToRemove.name} removed from subscription. Your new monthly total is $${response.data.newMonthlyTotal}/mo.`,
      });

      fetchSubscriptionStatus();
    } catch (error) {
      setShowRemoveLocationModal(false);
      setSelectedLocationToRemove(null);
      setMessage({
        type: "error",
        text:
          error.response?.data?.message ||
          "Failed to remove location from subscription",
      });
    }
  };

  const handleSubscribeNow = () => {
    navigate("/dashboard/settings/subscription/checkout");
  };

  // One state, decided by the API (utils/subscriptionAccessState.js), drives everything this
  // page says. The old predicate (`trialStatus !== "not_started" && !isInTrial &&
  // status !== "active"`) reported every non-active subscription — canceled, unpaid,
  // incomplete — as "your trial has ended".
  const accessView = getSubscriptionAccessView(subscription);
  const requiresSubscriptionPayment = accessView.blocking;

  const handleAccessAction = () => {
    switch (accessView.action) {
      case "reactivate":
        handleReactivateSubscription();
        return;
      case "add_card":
        navigate("/dashboard/settings/subscription/payment");
        return;
      case "checkout":
      default:
        handleSubscribeNow();
    }
  };

  const pricingModel = subscription?.pricing?.model || "base_plus_location";
  const plan = subscription?.pricing?.plan || null;
  const isPerProfessional = pricingModel === "per_professional" && Boolean(plan);

  const basePrice = subscription?.pricing?.businessMonthly ?? 65;
  const additionalPrice = subscription?.pricing?.businessAdditional ?? 45;
  const boothRenterPrice = subscription?.pricing?.boothRenterMonthly ?? 0;
  const boothRenterCount = subscription?.boothRenterCount ?? 0;
  const additionalLocationCount = Math.max(0, locations.length - 1);
  // Per-professional pricing is computed per location by the server; the flat figures no longer apply.
  const locationMonthlyTotal = isPerProfessional
    ? Number(plan.total || 0)
    : basePrice + additionalLocationCount * additionalPrice;
  const boothRenterMonthlyTotal = boothRenterCount * boothRenterPrice;
  const estimatedMonthlyTotal =
    locationMonthlyTotal + boothRenterMonthlyTotal;

  if (loading) {
    return (
      <SidebarLayout>
        <div
          className="business-subscription-page"
          style={{ padding: "40px", textAlign: "center" }}
        >
          <p style={{ fontSize: "16px", color: "#6b7280" }}>
            Loading subscription details...
          </p>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div
        className="business-subscription-page"
        style={{ maxWidth: "900px", margin: "0 auto", padding: "24px" }}
      >
        {/* Header */}
        <div style={{ marginBottom: "24px" }}>
          <button
            onClick={() => navigate("/dashboard")}
            style={{
              background: "none",
              border: "none",
              color: "#3b82f6",
              fontSize: "14px",
              cursor: "pointer",
              padding: "8px 0",
              marginBottom: "12px",
            }}
          >
            ← Back to Dashboard
          </button>
          <h1
            style={{
              fontSize: "28px",
              fontWeight: "700",
              margin: "0 0 8px",
              color: "#111827",
            }}
          >
            💼 Subscription Management
          </h1>
          <p style={{ fontSize: "14px", color: "#6b7280", margin: 0 }}>
            Manage your multi-location subscription and billing
          </p>
        </div>

        {/* Message Banner */}
        {message.text && (
          <div
            style={{
              background: message.type === "error" ? "#fef2f2" : "#f0fdf4",
              border: `1px solid ${message.type === "error" ? "#fecaca" : "#bbf7d0"}`,
              borderRadius: "8px",
              padding: "12px 16px",
              marginBottom: "24px",
              color: message.type === "error" ? "#dc2626" : "#10b981",
              fontSize: "14px",
            }}
          >
            {message.text}
          </div>
        )}

        {subscription && (
          <div
            style={{
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "20px",
              marginBottom: "24px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "12px",
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontSize: "17px",
                  fontWeight: "700",
                  color: "#111827",
                }}
              >
                Monthly Billing Breakdown
              </h2>
              <div style={{ fontSize: "12px", color: "#6b7280" }}>
                Based on active billable records
                {isPerProfessional &&
                  typeof subscription?.pricing?.currentBilledCents === "number" &&
                  subscription.pricing.currentBilledCents !== Math.round(estimatedMonthlyTotal * 100) && (
                    <>
                      {" · Stripe currently bills $"}
                      {(subscription.pricing.currentBilledCents / 100).toFixed(2)}
                      {" — the figures below apply after migration"}
                    </>
                  )}
              </div>
            </div>

            <div style={{ display: "grid", gap: "8px" }}>
              {isPerProfessional && (
                <>
                  {(plan.locations || []).map((loc) => (
                    <div
                      key={loc.locationId}
                      style={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <span style={{ color: "#374151", fontSize: "14px" }}>
                        {loc.name || "Location"} — {loc.professionalCount} professional
                        {loc.professionalCount === 1 ? "" : "s"}
                        {loc.atCap ? " (flat rate)" : ""}
                      </span>
                      <strong style={{ color: "#111827", fontSize: "14px" }}>
                        ${Number(loc.amount || 0).toFixed(2)}/mo
                      </strong>
                    </div>
                  ))}
                  {(plan.locations || []).length === 0 && (
                    <div style={{ fontSize: "13px", color: "#6b7280" }}>
                      No billable professionals yet — add one and the location starts billing.
                    </div>
                  )}
                </>
              )}

              {!isPerProfessional && (
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "#374151", fontSize: "14px" }}>
                    Base location
                  </span>
                  <strong style={{ color: "#111827", fontSize: "14px" }}>
                    ${basePrice.toFixed(2)}/mo
                  </strong>
                </div>
              )}

              {!isPerProfessional && additionalLocationCount > 0 && (
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span style={{ color: "#374151", fontSize: "14px" }}>
                    Additional locations ({additionalLocationCount} x ${additionalPrice.toFixed(2)})
                  </span>
                  <strong style={{ color: "#111827", fontSize: "14px" }}>
                    ${(additionalLocationCount * additionalPrice).toFixed(2)}/mo
                  </strong>
                </div>
              )}

              {boothRenterCount > 0 && (
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span style={{ color: "#374151", fontSize: "14px" }}>
                    Booth renters ({boothRenterCount} x ${boothRenterPrice.toFixed(2)})
                  </span>
                  <strong style={{ color: "#111827", fontSize: "14px" }}>
                    ${boothRenterMonthlyTotal.toFixed(2)}/mo
                  </strong>
                </div>
              )}

              <div
                style={{
                  borderTop: "1px solid #e5e7eb",
                  marginTop: "8px",
                  paddingTop: "12px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  style={{ color: "#111827", fontSize: "15px", fontWeight: "700" }}
                >
                  Total monthly subscription
                </span>
                <span
                  style={{ color: "#111827", fontSize: "22px", fontWeight: "800" }}
                >
                  ${estimatedMonthlyTotal.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Onboarding State - Trial Not Started Yet */}
        {subscription &&
          subscription.trialStatus === "not_started" &&
          subscription.status !== "active" && (
            <div
              style={{
                background: "linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)",
                color: "white",
                padding: "32px",
                borderRadius: "12px",
                marginBottom: "24px",
                textAlign: "center",
                boxShadow: "0 8px 24px rgba(59, 130, 246, 0.3)",
              }}
            >
              <div style={{ fontSize: "48px", marginBottom: "16px" }}>🚀</div>
              <h2
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  marginBottom: "12px",
                  margin: 0,
                }}
              >
                Welcome to OGESTYLE!
              </h2>
              <p
                style={{ fontSize: "16px", marginBottom: "8px", opacity: 0.95 }}
              >
                Complete your setup to start your free trial
              </p>
              <p
                style={{
                  fontSize: "14px",
                  marginBottom: "24px",
                  opacity: 0.85,
                }}
              >
                Your trial will begin when you activate your business. Take your
                time setting up:
                <br />✓ Connect Stripe • ✓ Add services • ✓ Invite staff • ✓
                Configure schedule
              </p>
              <button
                onClick={() => navigate("/dashboard")}
                style={{
                  background: "white",
                  color: "#1e40af",
                  border: "none",
                  padding: "16px 32px",
                  borderRadius: "8px",
                  fontSize: "16px",
                  fontWeight: "700",
                  cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                  transition: "transform 0.2s",
                }}
                onMouseEnter={(e) => (e.target.style.transform = "scale(1.05)")}
                onMouseLeave={(e) => (e.target.style.transform = "scale(1)")}
              >
                📋 Go to Dashboard
              </button>
            </div>
          )}

        {/* Access callout. The wording comes from accessState, so a cancellation reads as a
            cancellation and only a genuine trial expiry reads as a trial expiry. */}
        {requiresSubscriptionPayment && (
            <div
              style={{
                background: accessView.background,
                color: "white",
                padding: "32px",
                borderRadius: "12px",
                marginBottom: "24px",
                textAlign: "center",
                boxShadow: `0 8px 24px ${accessView.shadow || "rgba(0,0,0,0.2)"}`,
              }}
            >
              <div style={{ fontSize: "48px", marginBottom: "16px" }}>⚡</div>
              <h2
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  marginBottom: "12px",
                  margin: 0,
                }}
              >
                {accessView.title}
              </h2>
              <p
                style={{ fontSize: "16px", marginBottom: "8px", opacity: 0.95 }}
              >
                {accessView.message}
              </p>
              <p
                style={{
                  fontSize: "28px",
                  fontWeight: "700",
                  marginBottom: "24px",
                }}
              >
                ${estimatedMonthlyTotal.toFixed(2)}/month
              </p>
              <p
                style={{ fontSize: "14px", marginBottom: "24px", opacity: 0.9 }}
              >
                {locations.length} location{locations.length > 1 ? "s" : ""}
                {boothRenterCount > 0
                  ? ` • ${boothRenterCount} booth renter${boothRenterCount > 1 ? "s" : ""}`
                  : ""} •
                Cancel anytime
              </p>
              <button
                onClick={handleAccessAction}
                style={{
                  background: "white",
                  color: accessView.accent,
                  border: "none",
                  padding: "16px 32px",
                  borderRadius: "8px",
                  fontSize: "18px",
                  fontWeight: "700",
                  cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                  transition: "transform 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.transform = "scale(1.05)")
                }
                onMouseLeave={(e) =>
                  (e.target.style.transform = "scale(1)")
                }
              >
                💳 {accessView.ctaLabel}
              </button>
            </div>
          )}

        {/* In Trial - Optional Payment Collection */}
        {subscription &&
          !subscription.paymentMethodCollected &&
          subscription.isInTrial && (
            <div
              style={{
                background: "#f0f9ff",
                border: "2px solid #3b82f6",
                borderRadius: "12px",
                padding: "24px",
                marginBottom: "24px",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "36px", marginBottom: "12px" }}>💳</div>
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  marginBottom: "8px",
                  color: "#1e40af",
                }}
              >
                Add Payment Method (Optional)
              </h3>
              <p
                style={{
                  fontSize: "14px",
                  marginBottom: "16px",
                  color: "#3b82f6",
                }}
              >
                Add your payment method now to avoid interruption when your
                trial ends. You won't be charged until{" "}
                {subscription.trialEndDate &&
                  new Date(subscription.trialEndDate).toLocaleDateString()}
                .
              </p>
              <button
                onClick={() =>
                  navigate("/dashboard/settings/subscription/payment")
                }
                style={{
                  background: "#3b82f6",
                  color: "white",
                  border: "none",
                  padding: "12px 24px",
                  borderRadius: "8px",
                  fontSize: "15px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Add Payment Method
              </button>
            </div>
          )}

        {/* Subscription Status Component */}
        {subscription && subscription.status !== "active" && (
          <SubscriptionStatus
            subscriptionData={{
              subscriptionStatus: subscription.status,
              subscriptionInterval: subscription.interval,
              totalMonthlyAmount: subscription.totalMonthly,
              subscriptionCurrentPeriodEnd: subscription.currentPeriodEnd,
              cancelAtPeriodEnd: subscription.cancelAtPeriodEnd,
              isInTrial: subscription.isInTrial,
              trialDaysRemaining: subscription.trialDaysRemaining,
              trialEndDate: subscription.trialEndDate,
              inGracePeriod: subscription.inGracePeriod,
            }}
            userType="business"
          />
        )}

        {/* Location Management */}
        {locations.length > 0 && (
          <div
            style={{
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "24px",
              marginBottom: "24px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <div>
                <h2
                  style={{
                    fontSize: "18px",
                    fontWeight: "600",
                    margin: 0,
                    color: "#111827",
                  }}
                >
                  📍 Your Locations ({locations.length})
                </h2>
                {/* Only show payment warning when trial has actually expired */}
                {locations.some((loc) => !loc.linkedToBusinessSubscription) &&
                  subscription.trialStatus === "expired" && (
                    <p
                      style={{
                        margin: "4px 0 0",
                        fontSize: "12px",
                        color: "#f59e0b",
                        fontWeight: "500",
                      }}
                    >
                      ⚠️ Some locations are pending payment
                    </p>
                  )}
              </div>

              {subscription?.canAddLocations && (
                <button
                  onClick={() => navigate("/dashboard/locations?action=add")}
                  style={{
                    background: "#3b82f6",
                    color: "white",
                    border: "none",
                    padding: "8px 16px",
                    borderRadius: "6px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  + Add Location
                </button>
              )}
            </div>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              {locations.map((location) => {
                // Determine location state based on three trial states
                const isOnboarding = subscription.trialStatus === "not_started";
                const isInActiveTrial = subscription.trialStatus === "active";
                const isTrialExpired = subscription.trialStatus === "expired";

                const isSubscribed =
                  location.linkedToBusinessSubscription &&
                  location.subscriptionStatus === "active";

                // Location should be considered "active" during onboarding or trial
                const isTrialing =
                  (isOnboarding || isInActiveTrial) && !isSubscribed;

                // Only needs payment if trial has expired and not subscribed
                const needsPayment = isTrialExpired && !isSubscribed;

                // Legacy variable for backwards compatibility
                const isPendingPayment = needsPayment;
                const isActive = isSubscribed;

                return (
                  <div
                    key={location.id}
                    style={{
                      background: isTrialing
                        ? "#f0f9ff"
                        : isPendingPayment
                          ? "#fffbeb"
                          : "#f9fafb",
                      border: isTrialing
                        ? "2px solid #3b82f6"
                        : isPendingPayment
                          ? "2px solid #fbbf24"
                          : "1px solid #e5e7eb",
                      borderRadius: "8px",
                      padding: "16px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "12px",
                    }}
                  >
                    <div style={{ flex: "1 1 200px" }}>
                      <p
                        style={{
                          margin: "0 0 4px",
                          fontSize: "15px",
                          fontWeight: "600",
                          color: "#111827",
                        }}
                      >
                        {location.isBase
                          ? "🏢"
                          : isTrialing
                            ? "✨"
                            : isPendingPayment
                              ? "⏸️"
                              : "🏪"}{" "}
                        {location.name}
                      </p>
                      <p
                        style={{
                          margin: "0 0 4px",
                          fontSize: "13px",
                          color: "#6b7280",
                        }}
                      >
                        {location.city}, {location.state}
                      </p>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "12px",
                          fontWeight:
                            isPendingPayment || isTrialing ? "600" : "400",
                          color: isOnboarding
                            ? "#3b82f6"
                            : isInActiveTrial
                              ? "#10b981"
                              : isPendingPayment
                                ? "#f59e0b"
                                : isActive
                                  ? "#10b981"
                                  : "#6b7280",
                        }}
                      >
                        •{" "}
                        {isOnboarding
                          ? "🎯 Getting Started"
                          : isInActiveTrial
                            ? `✅ Trial Period - ${subscription.trialDaysRemaining > 0 ? `${subscription.trialDaysRemaining} days remaining` : "less than 1 day remaining"}`
                            : isPendingPayment
                              ? "⚠️ Payment Required"
                              : isActive
                                ? "Active"
                                : location.subscriptionStatus}
                      </p>
                    </div>
                    <div
                      style={{
                        textAlign: "right",
                        flex: "0 0 auto",
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      <div>
                        <p
                          style={{
                            margin: "0 0 4px",
                            fontSize: "18px",
                            fontWeight: "700",
                            color: "#3b82f6",
                          }}
                        >
                          ${location.monthlyAmount}/mo
                        </p>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "11px",
                            color: "#9ca3af",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          {isPerProfessional
                            ? location.professionals
                              ? `${location.professionals} professional${location.professionals === 1 ? "" : "s"}`
                              : ""
                            : location.isBase
                              ? "Base Price"
                              : "Additional"}
                        </p>
                      </div>
                      {/* Show remove button only if subscribed, not last location, and not canceled */}
                      {isSubscribed &&
                        locations.filter(
                          (loc) =>
                            loc.linkedToBusinessSubscription &&
                            loc.subscriptionStatus === "active",
                        ).length > 1 &&
                        !subscription.cancelAtPeriodEnd && (
                          <button
                            onClick={() => {
                              setSelectedLocationToRemove(location);
                              setShowRemoveLocationModal(true);
                            }}
                            style={{
                              padding: "6px 12px",
                              fontSize: "12px",
                              fontWeight: "500",
                              color: "#dc2626",
                              background: "white",
                              border: "1px solid #fecaca",
                              borderRadius: "4px",
                              cursor: "pointer",
                              transition: "all 0.2s",
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = "#fef2f2";
                              e.target.style.borderColor = "#dc2626";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = "white";
                              e.target.style.borderColor = "#fecaca";
                            }}
                          >
                            Remove
                          </button>
                        )}
                    </div>

                    {/* Show appropriate message based on trial state */}
                    {isOnboarding && (
                      <div style={{ flex: "1 1 100%", marginTop: "8px" }}>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "12px",
                            color: "#1e40af",
                            textAlign: "center",
                            background: "#dbeafe",
                            padding: "8px 12px",
                            borderRadius: "6px",
                          }}
                        >
                          🚀 Complete your business setup to start your free
                          trial and go live
                        </p>
                      </div>
                    )}
                    {isInActiveTrial && (
                      <div style={{ flex: "1 1 100%", marginTop: "8px" }}>
                        <p
                          style={{
                            margin: "0 0 8px",
                            fontSize: "12px",
                            color: "#065f46",
                            textAlign: "center",
                            background: "#d1fae5",
                            padding: "8px 12px",
                            borderRadius: "6px",
                          }}
                        >
                          ✅ Location is live and accepting bookings
                        </p>
                        {!subscription.paymentMethodCollected && (
                          <button
                            onClick={handleSubscribeNow}
                            disabled={checkoutLoading}
                            style={{
                              width: "100%",
                              padding: "8px 12px",
                              background: "white",
                              color: "#3b82f6",
                              border: "2px solid #3b82f6",
                              borderRadius: "6px",
                              fontSize: "12px",
                              fontWeight: "600",
                              cursor: checkoutLoading
                                ? "not-allowed"
                                : "pointer",
                              opacity: checkoutLoading ? 0.6 : 1,
                            }}
                          >
                            {checkoutLoading
                              ? "⏳ Processing..."
                              : "💳 Add Payment Method (Recommended)"}
                          </button>
                        )}
                      </div>
                    )}
                    {isPendingPayment && (
                      <div style={{ flex: "1 1 100%", marginTop: "8px" }}>
                        <button
                          onClick={handleSubscribeNow}
                          disabled={checkoutLoading}
                          style={{
                            width: "100%",
                            padding: "10px 16px",
                            background: checkoutLoading ? "#9ca3af" : "#f59e0b",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            fontSize: "14px",
                            fontWeight: "600",
                            cursor: checkoutLoading ? "not-allowed" : "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                          }}
                        >
                          {checkoutLoading
                            ? "⏳ Creating checkout..."
                            : "💳 Subscribe to Reactivate Location"}
                        </button>
                        <p
                          style={{
                            margin: "8px 0 0",
                            fontSize: "11px",
                            color: "#92400e",
                            textAlign: "center",
                          }}
                        >
                          Your trial has ended. Location is private until
                          subscription is active.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Billing History */}
        <div
          style={{
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            padding: "24px",
            marginBottom: "24px",
          }}
        >
          <h2
            style={{
              fontSize: "18px",
              fontWeight: "600",
              margin: "0 0 16px",
              color: "#111827",
            }}
          >
            📄 Billing & Invoices
          </h2>

          <button
            onClick={() => navigate("/dashboard/settings/invoices")}
            style={{
              width: "100%",
              padding: "14px",
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "500",
              color: "#374151",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            <span>📊</span>
            View Billing & Invoices
            <span style={{ fontSize: "12px", color: "#9ca3af" }}>→</span>
          </button>

          <p
            style={{
              marginTop: "12px",
              fontSize: "12px",
              color: "#6b7280",
              textAlign: "center",
            }}
          >
            Access your invoices, payment history, and update payment methods
          </p>
        </div>

        {/* Danger Zone */}
        {/* Subscription Management */}
        <div
          style={{
            background: "white",
            borderRadius: "12px",
            border: "1px solid #e5e7eb",
            padding: "24px",
          }}
        >
          <h2
            style={{
              fontSize: "18px",
              fontWeight: "600",
              margin: "0 0 16px",
              color: "#111827",
            }}
          >
            ⚙️ Manage Locations
          </h2>

          <p
            style={{
              fontSize: "14px",
              color: "#6b7280",
              margin: "0 0 16px",
              lineHeight: "1.6",
            }}
          >
            Your subscription is billed based on the number of active locations.
            To modify your subscription:
          </p>

          <ul
            style={{
              fontSize: "14px",
              color: "#6b7280",
              margin: "0 0 16px",
              paddingLeft: "20px",
              lineHeight: "1.8",
            }}
          >
            <li>
              <strong>Add locations:</strong> Go to Locations page and create a
              new location
            </li>
            <li>
              <strong>Remove locations:</strong> Click the "Remove" button on
              any location card above (keeps other locations active)
            </li>
            <li>
              <strong>Pause locations:</strong> Use the visibility toggle to
              temporarily hide a location from searches
            </li>
          </ul>

          {subscription?.cancelAtPeriodEnd && (
            <div
              style={{
                background: "#fef3c7",
                border: "1px solid #fbbf24",
                borderRadius: "8px",
                padding: "16px",
                marginBottom: "16px",
              }}
            >
              <p
                style={{
                  fontSize: "14px",
                  color: "#92400e",
                  margin: "0 0 12px",
                  fontWeight: "500",
                }}
              >
                ⚠️ Your subscription is scheduled to cancel on{" "}
                {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
              </p>
              <button
                onClick={handleReactivateSubscription}
                style={{
                  padding: "12px 24px",
                  background: "#10b981",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: "pointer",
                }}
              >
                🔄 Reactivate Subscription
              </button>
            </div>
          )}

          {!subscription?.cancelAtPeriodEnd &&
            subscription?.status === "active" && (
              <div
                style={{
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: "8px",
                  padding: "16px",
                  marginBottom: "16px",
                }}
              >
                <h3
                  style={{
                    fontSize: "15px",
                    fontWeight: "600",
                    margin: "0 0 8px",
                    color: "#991b1b",
                  }}
                >
                  Cancel Entire Subscription
                </h3>
                <p
                  style={{
                    fontSize: "13px",
                    color: "#7f1d1d",
                    margin: "0 0 8px",
                    lineHeight: "1.5",
                  }}
                >
                  <strong>
                    ⚠️ This will cancel ALL {locations.length} location
                    {locations.length > 1 ? "s" : ""}.
                  </strong>{" "}
                  All locations will remain active until the end of your billing
                  period, then switch to usage-based billing.
                </p>
                <p
                  style={{
                    fontSize: "12px",
                    color: "#92400e",
                    margin: "0 0 12px",
                    lineHeight: "1.4",
                    fontStyle: "italic",
                  }}
                >
                  💡 To remove only specific locations, use the "Remove" button
                  on each location card above.
                </p>
                <button
                  onClick={() => setShowCancelModal(true)}
                  style={{
                    padding: "10px 20px",
                    background: "#dc2626",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    fontSize: "14px",
                    fontWeight: "500",
                    cursor: "pointer",
                  }}
                >
                  Cancel All Locations
                </button>
              </div>
            )}

          <button
            onClick={() => navigate("/dashboard/locations")}
            style={{
              padding: "12px 24px",
              background: "#3b82f6",
              color: "white",
              border: "none",
              borderRadius: "6px",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
            }}
          >
            📍 Manage Locations
          </button>
        </div>

        {/* Cancel Confirmation Modal */}
        {showCancelModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
            onClick={() => setShowCancelModal(false)}
          >
            <div
              style={{
                background: "white",
                borderRadius: "12px",
                padding: "32px",
                maxWidth: "480px",
                width: "90%",
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ textAlign: "center", marginBottom: "24px" }}>
                <div style={{ fontSize: "48px", marginBottom: "16px" }}>⚠️</div>
                <h3
                  style={{
                    fontSize: "22px",
                    fontWeight: "700",
                    margin: "0 0 12px",
                    color: "#111827",
                  }}
                >
                  Cancel All {locations.length} Locations?
                </h3>
                <p
                  style={{
                    fontSize: "15px",
                    color: "#6b7280",
                    margin: "0 0 12px",
                    lineHeight: "1.6",
                  }}
                >
                  Your subscription will remain active until{" "}
                  <strong>
                    {new Date(
                      subscription.currentPeriodEnd,
                    ).toLocaleDateString()}
                  </strong>
                  . After that,{" "}
                  <strong>all {locations.length} locations</strong> will switch
                  to usage-based billing.
                </p>
                <p
                  style={{
                    fontSize: "13px",
                    color: "#dc2626",
                    margin: 0,
                    lineHeight: "1.5",
                    background: "#fef2f2",
                    padding: "12px",
                    borderRadius: "6px",
                  }}
                >
                  ⚠️{" "}
                  <strong>
                    All locations will stop appearing in public searches
                  </strong>{" "}
                  after cancellation. To keep some locations active, contact
                  support for selective removal.
                </p>
                use the "Remove" button on specific location cards above
              </div>
              <div style={{ display: "flex", gap: "12px" }}>
                <button
                  onClick={() => setShowCancelModal(false)}
                  style={{
                    flex: 1,
                    padding: "12px 24px",
                    fontSize: "15px",
                    fontWeight: "600",
                    border: "none",
                    borderRadius: "8px",
                    backgroundColor: "#10b981",
                    color: "white",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                >
                  Keep Subscription
                </button>
                <button
                  onClick={handleCancelSubscription}
                  style={{
                    flex: 1,
                    padding: "12px 24px",
                    fontSize: "15px",
                    fontWeight: "600",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    backgroundColor: "white",
                    color: "#dc2626",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                >
                  Yes, Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Remove Location Confirmation Modal */}
        {showRemoveLocationModal && selectedLocationToRemove && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
            onClick={() => {
              setShowRemoveLocationModal(false);
              setSelectedLocationToRemove(null);
            }}
          >
            <div
              style={{
                background: "white",
                borderRadius: "12px",
                padding: "32px",
                maxWidth: "520px",
                width: "90%",
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ textAlign: "center", marginBottom: "24px" }}>
                <div style={{ fontSize: "48px", marginBottom: "16px" }}>🗑️</div>
                <h3
                  style={{
                    fontSize: "22px",
                    fontWeight: "700",
                    margin: "0 0 12px",
                    color: "#111827",
                  }}
                >
                  Remove {selectedLocationToRemove.name}?
                </h3>
                <p
                  style={{
                    fontSize: "15px",
                    color: "#6b7280",
                    margin: "0 0 16px",
                    lineHeight: "1.6",
                  }}
                >
                  This location will be removed from your subscription{" "}
                  <strong>immediately</strong>. You'll receive a prorated credit
                  for the unused portion of this billing period.
                </p>
              </div>

              <div
                style={{
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: "8px",
                  padding: "16px",
                  marginBottom: "24px",
                }}
              >
                <h4
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    margin: "0 0 12px",
                    color: "#991b1b",
                  }}
                >
                  What happens after removal:
                </h4>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: "20px",
                    fontSize: "13px",
                    color: "#7f1d1d",
                    lineHeight: "1.6",
                  }}
                >
                  <li>
                    Location switches to usage-based billing (5% per
                    booking)
                  </li>
                  <li>
                    <strong>Location stops appearing in public searches</strong>
                  </li>
                  <li>Your monthly billing is updated immediately</li>
                  <li>
                    You receive a credit for $
                    {selectedLocationToRemove.monthlyAmount}/mo
                  </li>
                  <li>Other locations remain active on subscription</li>
                </ul>
              </div>

              <div
                style={{
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: "8px",
                  padding: "12px",
                  marginBottom: "24px",
                  fontSize: "13px",
                  color: "#15803d",
                  lineHeight: "1.5",
                }}
              >
                💰 <strong>New Monthly Total:</strong> $
                {subscription.totalMonthly -
                  selectedLocationToRemove.monthlyAmount}
                /mo (
                {locations.filter(
                  (loc) =>
                    loc.linkedToBusinessSubscription &&
                    loc.subscriptionStatus === "active",
                ).length - 1}{" "}
                locations)
              </div>

              <div style={{ display: "flex", gap: "12px" }}>
                <button
                  onClick={() => {
                    setShowRemoveLocationModal(false);
                    setSelectedLocationToRemove(null);
                  }}
                  style={{
                    flex: 1,
                    padding: "12px 24px",
                    fontSize: "15px",
                    fontWeight: "600",
                    border: "none",
                    borderRadius: "8px",
                    backgroundColor: "#10b981",
                    color: "white",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                >
                  Keep Location
                </button>
                <button
                  onClick={handleRemoveLocation}
                  style={{
                    flex: 1,
                    padding: "12px 24px",
                    fontSize: "15px",
                    fontWeight: "600",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    backgroundColor: "white",
                    color: "#dc2626",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                >
                  Yes, Remove Location
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </SidebarLayout>
  );
};

export default BusinessSubscription;
